<template>
  <div class="transacoes-list">
    <v-card>
      <v-card-title class="d-flex justify-space-between">
        <span>{{ titulo }}</span>
        <v-btn
          v-if="mostrarBotaoVerMais"
          variant="text"
          color="primary"
          :to="{ name: 'transactions' }"
          size="small"
        >
          Ver Todas
          <v-icon right>mdi-chevron-right</v-icon>
        </v-btn>
      </v-card-title>
      
      <v-divider></v-divider>
      
      <v-card-text class="pa-0">
        <v-data-table
          :headers="cabecalhos"
          :items="transacoes"
          :loading="carregando"
          loading-text="Carregando transações..."
          no-data-text="Nenhuma transação encontrada"
          class="transacoes-table"
          hover
          density="compact"
          :items-per-page="itemsPorPagina"
          :hide-default-footer="esconderPaginacao"
        >
          <!-- Hash da Transação -->
          <template v-slot:item.hash="{ item }">
            <router-link 
              :to="{ name: 'transaction-details', params: { hash: item.hash } }"
              class="text-primary text-decoration-none"
            >
              <div class="text-truncate">
                {{ formatarHash(item.hash) }}
              </div>
            </router-link>
          </template>
          
          <!-- Bloco -->
          <template v-slot:item.blockNumber="{ item }">
            <router-link 
              :to="{ name: 'block-details', params: { id: item.blockNumber } }"
              class="text-decoration-none"
            >
              {{ item.blockNumber }}
            </router-link>
          </template>
          
          <!-- Timestamp -->
          <template v-slot:item.timestamp="{ item }">
            <div class="d-flex align-center">
              <v-icon size="small" class="mr-1">mdi-clock-outline</v-icon>
              <v-tooltip location="top">
                <template v-slot:activator="{ props }">
                  <span v-bind="props">{{ tempoRelativo(item.timestamp) }}</span>
                </template>
                {{ formatarData(item.timestamp) }}
              </v-tooltip>
            </div>
          </template>
          
          <!-- Remetente (From) -->
          <template v-slot:item.from="{ item }">
            <router-link 
              :to="{ name: 'account-details', params: { address: item.from } }"
              class="text-decoration-none"
            >
              <div class="text-truncate">
                {{ formatarHash(item.from) }}
              </div>
            </router-link>
          </template>
          
          <!-- Direção -->
          <template v-slot:item.direction="{ item }">
            <v-icon size="small" color="primary">mdi-arrow-right</v-icon>
          </template>
          
          <!-- Destinatário (To) -->
          <template v-slot:item.to="{ item }">
            <router-link 
              :to="{ name: 'account-details', params: { address: item.to } }"
              class="text-decoration-none"
            >
              <div class="text-truncate">
                {{ formatarHash(item.to) }}
              </div>
            </router-link>
          </template>
          
          <!-- Valor -->
          <template v-slot:item.value="{ item }">
            <span class="font-weight-medium">
              {{ formatarValorToken(item.value) }}
            </span>
          </template>
          
          <!-- Gas Usado -->
          <template v-slot:item.gasUsed="{ item }">
            <span class="text-body-2">
              {{ formatarGasUsed(item.gasUsed) }}
            </span>
          </template>
          
          <!-- Status -->
          <template v-slot:item.status="{ item }">
            <v-chip
              size="x-small"
              :color="statusColor(item.status)"
              class="font-weight-medium"
              :text="statusLabel(item.status)"
            ></v-chip>
          </template>
          
          <!-- Ações -->
          <template v-slot:item.actions="{ item }">
            <div class="d-flex justify-end">
              <v-btn
                icon
                variant="text"
                density="comfortable"
                size="small"
                :to="{ name: 'transaction-details', params: { hash: item.hash } }"
              >
                <v-icon>mdi-information-outline</v-icon>
              </v-btn>
            </div>
          </template>
          
          <!-- Carregando -->
          <template v-slot:loading>
            <v-skeleton-loader
              v-for="n in 5"
              :key="n"
              type="table-row"
              class="my-2"
            ></v-skeleton-loader>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { formatarHash, formatarData, tempoRelativo, formatarValorToken } from '@/services/utilidades'

export default {
  name: 'TransacoesList',
  props: {
    titulo: {
      type: String,
      default: 'Transações Recentes'
    },
    transacoes: {
      type: Array,
      default: () => []
    },
    carregando: {
      type: Boolean,
      default: false
    },
    esconderPaginacao: {
      type: Boolean,
      default: false
    },
    mostrarBotaoVerMais: {
      type: Boolean,
      default: true
    },
    mostrarColunas: {
      type: Array,
      default: () => ['hash', 'blockNumber', 'timestamp', 'from', 'direction', 'to', 'value', 'gasUsed', 'status', 'actions']
    },
    itemsPorPagina: {
      type: Number,
      default: 10
    }
  },
  data() {
    return {
      todasColunas: [
        { 
          title: 'Hash', 
          key: 'hash', 
          sortable: false, 
          align: 'start',
          width: '140px'
        },
        { 
          title: 'Bloco', 
          key: 'blockNumber', 
          sortable: true,
          align: 'start',
          width: '80px'
        },
        { 
          title: 'Tempo', 
          key: 'timestamp', 
          sortable: true,
          align: 'start',
          width: '120px'
        },
        { 
          title: 'De', 
          key: 'from', 
          sortable: false,
          align: 'start',
          width: '140px'
        },
        { 
          title: '', 
          key: 'direction', 
          sortable: false,
          align: 'center',
          width: '40px'
        },
        { 
          title: 'Para', 
          key: 'to', 
          sortable: false,
          align: 'start',
          width: '140px'
        },
        { 
          title: 'Valor', 
          key: 'value', 
          sortable: true,
          align: 'end',
          width: '120px'
        },
        { 
          title: 'Gas Usado', 
          key: 'gasUsed', 
          sortable: true,
          align: 'end',
          width: '100px'
        },
        { 
          title: 'Status', 
          key: 'status', 
          sortable: false,
          align: 'center',
          width: '100px'
        },
        { 
          title: '', 
          key: 'actions', 
          sortable: false,
          align: 'end',
          width: '50px'
        }
      ],
    }
  },
  computed: {
    cabecalhos() {
      return this.todasColunas.filter(col => this.mostrarColunas.includes(col.key))
    }
  },
  methods: {
    formatarHash,
    formatarData,
    tempoRelativo,
    formatarValorToken,
    
    /**
     * Formata o valor de gas_used para exibição.
     * Garante que zero é exibido como "0" e não omitido.
     */
    formatarGasUsed(gasUsed) {
      if (gasUsed == null) return '-'
      return gasUsed.toLocaleString('pt-BR')
    },
    
    /**
     * Retorna a cor do chip de status da transação.
     * Verde para confirmada/success, vermelho para falha/failed.
     */
    statusColor(status) {
      if (status === 'confirmed' || status === 'success' || status === 'confirmada') {
        return 'success'
      }
      if (status === 'failed' || status === 'falha') {
        return 'error'
      }
      return 'warning'
    },
    
    /**
     * Retorna o label do chip de status da transação.
     */
    statusLabel(status) {
      if (status === 'confirmed' || status === 'success' || status === 'confirmada') {
        return 'Confirmada'
      }
      if (status === 'failed' || status === 'falha') {
        return 'Falha'
      }
      return 'Pendente'
    }
  }
}
</script>

<style scoped>
.transacoes-table {
  width: 100%;
}
</style>
